/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Injectable, isDevMode} from '@angular/core';
import {HammerGestureConfig} from '@angular/platform-browser';
import {HammerStatic, HammerInstance, Recognizer, RecognizerStatic} from './gesture-annotations';
export class GestureConfig extends HammerGestureConfig {
private _hammer: HammerStatic = typeof window !== 'undefined' ? ( /** @type {?} */((window as any))).Hammer : null;

  /* List of new event names to add to the gesture support list */
  events: string[] = this._hammer ? [
    'longpress',
    'slide',
    'slidestart',
    'slideend',
    'slideright',
    'slideleft'
  ] : [];
constructor() {
    super();

    if (!this._hammer && isDevMode()) {
      console.warn(
        'Could not find HammerJS. Certain Angular Material ' +
        'components may not work correctly.'
      );
    }
  }
/**
 * Builds Hammer instance manually to add custom recognizers that match the Material Design spec.
 * 
 * Our gesture names come from the Material Design gestures spec:
 * https://www.google.com/design/spec/patterns/gestures.html#gestures-touch-mechanics
 * 
 * More information on default recognizers can be found in Hammer docs:
 * http://hammerjs.github.io/recognizer-pan/
 * http://hammerjs.github.io/recognizer-press/
 * 
 * @param {?} element Element to which to assign the new HammerJS gestures.
 * @return {?} Newly-created HammerJS instance.
 */
buildHammer(element: HTMLElement): HammerInstance {
    const /** @type {?} */ mc = new this._hammer(element);

    // Default Hammer Recognizers.
    let /** @type {?} */ pan = new this._hammer.Pan();
    let /** @type {?} */ swipe = new this._hammer.Swipe();
    let /** @type {?} */ press = new this._hammer.Press();

    // Notice that a HammerJS recognizer can only depend on one other recognizer once.
    // Otherwise the previous `recognizeWith` will be dropped.
    // TODO: Confirm threshold numbers with Material Design UX Team
    let /** @type {?} */ slide = this._createRecognizer(pan, {event: 'slide', threshold: 0}, swipe);
    let /** @type {?} */ longpress = this._createRecognizer(press, {event: 'longpress', time: 500});

    // Overwrite the default `pan` event to use the swipe event.
    pan.recognizeWith(swipe);

    // Add customized gestures to Hammer manager
    mc.add([swipe, press, pan, slide, longpress]);

    return /** @type {?} */(( mc as HammerInstance));
  }
/**
 * Creates a new recognizer, without affecting the default recognizers of HammerJS
 * @param {?} base
 * @param {?} options
 * @param {...?} inheritances
 * @return {?}
 */
private _createRecognizer(base: Recognizer, options: any, ...inheritances: Recognizer[]) {
    let /** @type {?} */ recognizer = new ( /** @type {?} */((base.constructor as RecognizerStatic)))(options);

    inheritances.push(base);
    inheritances.forEach(item => recognizer.recognizeWith(item));

    return recognizer;
  }

static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function GestureConfig_tsickle_Closure_declarations() {
/** @type {?} */
GestureConfig.decorators;
/**
 * @nocollapse
 * @type {?}
 */
GestureConfig.ctorParameters;
/** @type {?} */
GestureConfig.prototype._hammer;
/** @type {?} */
GestureConfig.prototype.events;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
